import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { listStatusPages, createStatusPage, deleteStatusPage, StatusPageParams } from "../lib/api";
import { StatusPage } from "../types";
import { usePlan } from "../App";
import { Bone } from "../components/Skeleton";

const STATUS_PAGE_LIMIT: Record<string, number> = { free: 1, pro: 5 };

export default function StatusPages() {
  const qc = useQueryClient();
  const plan = usePlan();
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["status-pages"],
    queryFn: listStatusPages,
  });

  const deleteMut = useMutation({
    mutationFn: deleteStatusPage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["status-pages"] }),
  });

  const pages: StatusPage[] = data?.pages ?? [];
  const limit = STATUS_PAGE_LIMIT[plan] ?? 1;
  const atLimit = pages.length >= limit;

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 700, color: "#EFF2F5" }}>Status Pages</h1>
            <p style={{ margin: 0, fontSize: 13, color: "#98A1AB" }}>
              {pages.length} of {limit} status page{limit === 1 ? "" : "s"} used
              {plan === "free" && (
                <> · <Link to="/upgrade" style={{ color: "#FF2566", textDecoration: "none" }}>Upgrade for more</Link></>
              )}
            </p>
          </div>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="btn-primary"
            style={{ padding: "10px 20px", fontSize: 14, opacity: atLimit && !showForm ? 0.5 : 1 }}
            disabled={atLimit && !showForm}
            title={atLimit ? `Status page limit reached (${limit})` : undefined}
          >
            {showForm ? "Cancel" : "+ New Page"}
          </button>
        </div>

        {showForm && (
          <CreateForm
            onSuccess={() => { setShowForm(false); qc.invalidateQueries({ queryKey: ["status-pages"] }); }}
          />
        )}

        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[170, 210, 150].map((nameW, i) => (
              <div key={i} className="glass" style={{ padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <Bone w={nameW} h={15} />
                  <Bone w={nameW - 30} h={11} />
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <Bone w={48} h={28} />
                  <Bone w={64} h={28} />
                  <Bone w={54} h={28} />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
            <p style={{ fontSize: 16, color: "#EFF2F5" }}>Service temporarily unavailable</p>
            <p style={{ fontSize: 13 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
          </div>
        ) : pages.length === 0 && !showForm ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#98A1AB" }}>
            <p style={{ fontSize: 16 }}>No status pages yet.</p>
            <p style={{ fontSize: 13 }}>Create a page to share uptime and latency with your users.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pages.map((pg) => (
              <div key={pg.id} className="glass" style={{ padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: "#EFF2F5", marginBottom: 2 }}>{pg.title}</div>
                  <div style={{ fontSize: 12, color: "#98A1AB" }}>
                    /status/{pg.slug} · {pg.monitors.length} monitor{pg.monitors.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <a
                    href={`/status/${pg.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-glass"
                    style={{ fontSize: 13, padding: "6px 12px" }}
                  >
                    View
                  </a>
                  <Link
                    to={`/dashboard/status-pages/${pg.id}`}
                    className="btn-glass"
                    style={{ fontSize: 13, padding: "6px 12px" }}
                  >
                    Manage
                  </Link>
                  <button
                    onClick={() => {
                      if (confirm(`Delete "${pg.title}"?`)) deleteMut.mutate(pg.id);
                    }}
                    className="btn-run"
                    style={{ fontSize: 13, padding: "6px 12px" }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface CreateFormValues {
  title: string;
  slug: string;
}

function CreateForm({ onSuccess }: { onSuccess: () => void }) {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<CreateFormValues>();
  const [apiError, setApiError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (params: StatusPageParams) => createStatusPage(params),
    onSuccess,
    onError: (e) => setApiError(e instanceof Error ? e.message : "Failed to create status page"),
  });

  function toSlug(v: string) {
    return v.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  }

  function onSubmit(values: CreateFormValues) {
    mut.mutate({
      title: values.title,
      slug: values.slug,
      is_public: true,
      monitors: [],
    });
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="glass"
      style={{ padding: 24, marginBottom: 24, display: "flex", flexDirection: "column", gap: 14 }}
    >
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#EFF2F5" }}>New Status Page</h3>
      {apiError && <p style={{ color: "#FF5C5C", fontSize: 13, margin: 0 }}>{apiError}</p>}

      <div>
        <label style={labelStyle}>Title</label>
        <input
          {...register("title", { required: "Title is required" })}
          className="input-dark"
          placeholder="Acme Status"
          onChange={(e) => {
            setValue("title", e.target.value);
            setValue("slug", toSlug(e.target.value));
          }}
        />
        {errors.title && <p style={errorStyle}>{errors.title.message}</p>}
      </div>

      <div>
        <label style={labelStyle}>Slug</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#98A1AB", whiteSpace: "nowrap" }}>worldrtt.com/status/</span>
          <input
            {...register("slug", {
              required: "Slug is required",
              pattern: { value: /^[a-z0-9][a-z0-9-]{1,58}[a-z0-9]$/, message: "3–60 chars, lowercase letters, numbers, hyphens" },
            })}
            className="input-dark"
            placeholder="acme-status"
          />
        </div>
        {errors.slug && <p style={errorStyle}>{errors.slug.message}</p>}
        {!errors.slug && watch("slug") && (
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#98A1AB" }}>
            worldrtt.com/status/{watch("slug")}
          </p>
        )}
      </div>

      <p style={{ margin: 0, fontSize: 12, color: "#98A1AB" }}>
        You can add monitors and customize display names after creating the page.
      </p>

      <button type="submit" disabled={mut.isPending} className="btn-primary" style={{ padding: "11px 0", fontSize: 14 }}>
        {mut.isPending ? "Creating…" : "Create Status Page"}
      </button>
    </form>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#98A1AB",
  marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em",
};
const errorStyle: React.CSSProperties = { margin: "4px 0 0", fontSize: 12, color: "#FF5C5C" };
